import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    Table,
    Spinner,
    Button,
    FormGroup,
    Label,
    Input,
    FormText
} from 'reactstrap';
import { toast } from 'react-toastify';
import { apiURL1 } from '../../config';
import Header from '../../components/Headers/Header';
import Footer from 'components/Footers/Footer';

const baseURL = apiURL1;

function AddSubjectResult() {
    const token = localStorage.getItem("token");

    const { classId, subjectId, examId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const { className, subjectName, teacherName, type, typeId, year } = location.state || {};
    // console.log('Type is ', typeId)

    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalMarks, setTotalMarks] = useState('');
    const [results, setResults] = useState({});
    const [errors, setErrors] = useState({});
    const [toastDisplayed, setToastDisplayed] = useState(false);

    useEffect(() => {
        fetchData();
    }, [classId, subjectId, type, year]);

    const fetchData = async () => {
        setLoading(true);
        try {
            // Attempt to fetch results from the primary API
            const resultsResponse = await axios.get(
                `${baseURL}/superadmin/results/subject/${subjectId}/type/${typeId}/year/${year}/${classId}`
                , {
                    headers: {
                        'Authorization': `Bearer ${token}`, // Include the token in the headers
                    }
                });

            if (resultsResponse.data.data && resultsResponse.data.data.length > 0) {
                const resultData = resultsResponse.data.data[0];
                const studentData = resultData.students;

                setStudents(studentData.map(s => ({
                    ...s.student,
                    marksObtained: s.marksObtained,
                    // grade: s.grade,
                    remarks: s.remarks,
                    picture: s.student.picture // Ensure picture is also included
                })));
                setTotalMarks(resultData.totalMarks || '');

                const initialResults = {};
                studentData.forEach(student => {
                    initialResults[student.student._id] = {
                        marksObtained: student.marksObtained || '',
                        remarks: student.remarks || '',
                        // grade: student.grade || ''
                    };
                });
                setResults(initialResults);
                setLoading(false);
                return; // Data found, exit the function
            }
        } catch (error) {
            console.error('Error fetching results:', error);
            toast.error('Error fetching results. Trying to fetch student list.');
        }

        // If primary API fails or returns no data, fetch student list
        try {
            const studentResponse = await axios.get(`${baseURL}/superadmin/getStudentsByClass/${classId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`, // Include the token in the headers
                }
            });
            setStudents(studentResponse.data.data.map(student => ({
                ...student,
                marksObtained: '',
                // grade: '',
                remarks: '',
                picture: student.picture // Include picture here as well
            })));
            setTotalMarks('');
            const initialResults = {};
            studentResponse.data.data.forEach(student => {
                initialResults[student._id] = {
                    marksObtained: '',
                    remarks: '',
                    // grade: ''
                };
            });
            setResults(initialResults);
        } catch (error) {
            console.error('Error fetching students:', error);
            toast.error('Error fetching students.');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (studentId, field, value) => {
        let parsedValue = value;
        if (field === 'marksObtained') {
            parsedValue = Math.max(0, value);
        }

        setResults(prevResults => ({
            ...prevResults,
            [studentId]: {
                ...prevResults[studentId],
                [field]: parsedValue
            }
        }));
    };

    const validate = () => {
        let isValid = true;
        const newErrors = {};
        let errorMessage = null;

        if (!totalMarks) {
            newErrors.totalMarks = 'Total marks are required';
            errorMessage = 'Total marks are required';
            isValid = false;
        } else if (isNaN(totalMarks)) {
            newErrors.totalMarks = 'Total marks must be a number';
            errorMessage = 'Total marks must be a number';
            isValid = false;
        }

        Object.keys(results).forEach(studentId => {
            const marksObtained = results[studentId].marksObtained;
            if (!marksObtained) {
                newErrors[studentId] = { ...newErrors[studentId], marksObtained: 'Obtained marks are required' };
                errorMessage = 'Obtained marks are required';
                isValid = false;
            } else if (isNaN(marksObtained)) {
                newErrors[studentId] = { ...newErrors[studentId], marksObtained: 'Obtained marks must be a number' };
                isValid = false;
            } else if (parseFloat(marksObtained) > parseFloat(totalMarks)) {
                newErrors[studentId] = { ...newErrors[studentId], marksObtained: 'Obtained marks cannot be greater than total marks' };
                errorMessage = 'Obtained marks cannot be greater than total marks';
                isValid = false;
            }
        });

        setErrors(newErrors);

        if (errorMessage && !toastDisplayed) {
            toast.error(errorMessage);
            setToastDisplayed(true);
        }

        return isValid;
    };

    const handleSubmit = async () => {
        setToastDisplayed(false);

        if (validate()) {
            setLoading(true);
            try {
                const resultsArray = students.map(student => ({
                    studentId: student._id,
                    studentName: student.fullName,
                    marksObtained: parseFloat(results[student._id].marksObtained),
                    // grade: results[student._id].grade,
                    remarks: results[student._id].remarks
                }));

                const payload = {
                    examId: examId,
                    subjectId: subjectId,
                    classId: classId,
                    results: resultsArray,
                    totalMarks: parseFloat(totalMarks)
                };

                await axios.post(`${baseURL}/superadmin/results`, payload, {
                    headers: {
                        'Authorization': `Bearer ${token}`, // Include the token in the headers
                    }
                });
                toast.success('Results uploaded successfully!');
                navigate(`/admin/subject-result/${examId}`);
            } catch (error) {
                console.error('Error uploading results:', error);
                toast.error('Error uploading results');
            } finally {
                setLoading(false);
            }
        }
    };

    if (loading) {
        return (
            <>
                <Header />
                <Container>
                    <Row>
                        <Col>
                            <div style={{ display: 'flex', justifyContent: 'center' }}>
                                <Spinner color="primary" />
                            </div>
                        </Col>
                    </Row>
                </Container>
            </>
        );
    }

    return (
        <>
            <Header />
            <Container>
                <Row>
                    <Col>
                        <Card>
                            <CardHeader style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <h3>Add Subject Result</h3>
                                <Button color="primary" onClick={handleSubmit} disabled={loading}>
                                    {loading ? <Spinner size="sm" /> : 'Upload'}
                                </Button>
                            </CardHeader>
                            <CardBody>
                                <h4>Class: {className}</h4>
                                <h4>Subject: {subjectName}</h4>
                                <h4>Staff: {teacherName}</h4>

                                <FormGroup>
                                    <Label for="totalMarks">Total Marks</Label>
                                    <Input
                                        type="number"
                                        name="totalMarks"
                                        id="totalMarks"
                                        placeholder="Enter total marks"
                                        value={totalMarks}
                                        onChange={(e) => setTotalMarks(e.target.value)}
                                        invalid={!!errors.totalMarks}
                                        min="0"
                                    />
                                    {errors.totalMarks && <FormText color="danger">{errors.totalMarks}</FormText>}
                                </FormGroup>

                                <div className="table-responsive">
                                    <Table className="table">
                                        <thead>
                                            <tr style={{ backgroundColor: '#2980b9', color: 'white' }}>
                                                <th>Picture</th>
                                                <th>Reg Number</th>
                                                <th>Name</th>
                                                <th>Obtained Marks</th>
                                                {/* <th>Grade</th> */}
                                                <th>Comments</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {students.map(student => (
                                                <tr key={student._id}>
                                                    <td>
                                                        {student.picture ? (
                                                            <img
                                                                src={student.picture}
                                                                alt="Profile"
                                                                style={{ width: '50px', height: '50px', borderRadius: '50%' }}
                                                            />
                                                        ) : (
                                                            <div style={{
                                                                width: '50px',
                                                                height: '50px',
                                                                borderRadius: '50%',
                                                                backgroundColor: '#ddd',
                                                                display: 'flex',
                                                                justifyContent: 'center',
                                                                alignItems: 'center'
                                                            }}>
                                                                No Image
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td>{student.registrationNumber}</td>
                                                    <td>{student.fullName}</td>
                                                    <td>
                                                        <Input
                                                            type="number"
                                                            name={`marksObtained-${student._id}`}
                                                            id={`marksObtained-${student._id}`}
                                                            placeholder="Enter obtained marks"
                                                            value={results[student._id]?.marksObtained || ''}
                                                            onChange={(e) => handleInputChange(student._id, 'marksObtained', e.target.value)}
                                                            invalid={!!errors[student._id]?.marksObtained}
                                                            min="0"
                                                        />
                                                        {errors[student._id]?.marksObtained && <FormText color="danger">{errors[student._id].marksObtained}</FormText>}
                                                    </td>
                                                    {/* <td>
                                                        <Input
                                                            type="text"
                                                            name={`grade-${student._id}`}
                                                            id={`grade-${student._id}`}
                                                            placeholder="Enter grade"
                                                            value={results[student._id]?.grade || ''}
                                                            onChange={(e) => handleInputChange(student._id, 'grade', e.target.value)}
                                                        />
                                                    </td> */}
                                                    <td>
                                                        <Input
                                                            type="text"
                                                            name={`remarks-${student._id}`}
                                                            id={`remarks-${student._id}`}
                                                            placeholder="Enter comments"
                                                            value={results[student._id]?.remarks || ''}
                                                            onChange={(e) => handleInputChange(student._id, 'remarks', e.target.value)}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
                <Footer />
            </Container>
        </>
    );
}

export default AddSubjectResult;
